import type { GetMessagesQueryVariables } from '#src/graphql/generated';
import type { ApolloClient } from '@apollo/client';

import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_AI_THREAD_REPLY } from '#src/graphql/mutations/create-ai-thread-reply';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';

export function formatProcessorFailureMessage(errorMessage: string): string {
  const t = errorMessage.trim();
  if (!t) return 'Error: Processor failed';
  return t.startsWith('Error:') ? t : `Error: ${t}`;
}

export async function persistProcessorFailureAsThreadReply(
  client: ApolloClient,
  rootMessageId: string,
  errorMessage: string,
  processorInstanceId?: string | null,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const pid = processorInstanceId?.trim();
  try {
    await client.mutate({
      mutation: CREATE_AI_THREAD_REPLY,
      variables: {
        input: {
          rootMessageId,
          value: formatProcessorFailureMessage(errorMessage),
          ...(pid ? { processorInstanceId: pid } : {}),
        },
      },
    });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Failed to persist error reply',
    };
  }
}

export async function persistProcessorFailureAsDocumentRootMessage(
  client: ApolloClient,
  params: {
    documentId: string;
    errorMessage: string;
    processorInstanceId?: string | null;
    getMessagesVariables: GetMessagesQueryVariables;
  },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const contexts: Array<{ name: string; value: string }> = [
    { name: 'documentId', value: params.documentId },
  ];
  const pid = params.processorInstanceId?.trim();
  if (pid) {
    contexts.push({ name: 'processorInstanceId', value: pid });
  }
  try {
    await client.mutate({
      mutation: CREATE_MESSAGE,
      variables: {
        input: {
          value: formatProcessorFailureMessage(params.errorMessage),
          contexts,
        },
      },
      update(cache, { data }) {
        if (data?.createMessage) {
          mergeCreateMessageIntoGetMessagesCache(
            cache,
            data.createMessage,
            params.getMessagesVariables,
          );
        }
      },
    });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Failed to persist error message',
    };
  }
}

export async function persistProcessorFailureAsGlobalRootMessage(
  client: ApolloClient,
  params: {
    errorMessage: string;
    processorInstanceId?: string | null;
  },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const variables: GetMessagesQueryVariables = {};
  const pid = params.processorInstanceId?.trim();
  try {
    await client.mutate({
      mutation: CREATE_MESSAGE,
      variables: {
        input: {
          value: formatProcessorFailureMessage(params.errorMessage),
          ...(pid
            ? {
                contexts: [{ name: 'processorInstanceId', value: pid }],
              }
            : {}),
        },
      },
      update(cache, { data }) {
        if (data?.createMessage) {
          mergeCreateMessageIntoGetMessagesCache(
            cache,
            data.createMessage,
            variables,
          );
        }
      },
    });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Failed to persist error message',
    };
  }
}
